/**
 * Features — bento-style grid of 4 product capabilities.
 *
 * Each card has an icon, title, body. The top-left is wide (col-span-2
 * on md+) to break the rhythm and give the section a focal point.
 */
import { useTranslation } from 'react-i18next'
import { Zap, Shield, Layers, Bell } from 'lucide-react'

const FEATURES = [
  { key: 'execution',  icon: Zap,    className: 'md:col-span-2' },
  { key: 'risk',       icon: Shield, className: '' },
  { key: 'strategies', icon: Layers, className: '' },
  { key: 'alerts',     icon: Bell,   className: 'md:col-span-2' },
] as const

export function Features() {
  const { t } = useTranslation('landing')
  return (
    <section id="features" className="px-6 py-20 md:py-28 scroll-mt-16">
      <div className="max-w-6xl mx-auto">
        <Header
          label={t('features.section_label')}
          title={t('features.title')}
          subtitle={t('features.subtitle')}
        />

        <div className="mt-12 grid md:grid-cols-3 gap-px bg-line">
          {FEATURES.map(({ key, icon: Icon, className }) => (
            <article
              key={key}
              className={`bg-ink p-6 md:p-8 ${className}`}
            >
              <div className="w-9 h-9 grid place-items-center bg-fire-faint rounded mb-5">
                <Icon size={18} className="text-fire" />
              </div>
              <h3 className="text-lg font-semibold tracking-tight">
                {t(`features.items.${key}.title`)}
              </h3>
              <p className="mt-2 text-sm text-textDim leading-relaxed">
                {t(`features.items.${key}.body`)}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ─── Section header (reused by other sections) ──────────────────── */

interface HeaderProps { label: string; title: string; subtitle?: string }

export function Header({ label, title, subtitle }: HeaderProps) {
  return (
    <div className="max-w-2xl">
      <div className="text-xs uppercase tracking-[0.2em] text-fire mb-3">
        {label}
      </div>
      <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 text-base text-textDim leading-relaxed">
          {subtitle}
        </p>
      )}
    </div>
  )
}
